import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from '@react-hook/media-query';
import { useRouter } from 'next/router';

import { scrollToTargetOffset, setBodyNoScroll } from '../../utils/common';
import FilterSelect from '../FilterSelect';
import ReportCard from '../Cards/reportCard';
import Pagination from '../Pagination';
import CustomSelect from '../CustomSelect';

import { getFilters, getReports, getReportsLoading } from '../../redux/reports/reports.selector';
import { addFilter, removeFilter, clearFilters, fetchReports } from '../../redux/reports/reports.action';

import style from './ReportListing.module.scss';

const ReportListing = ({ filters, reports, copies }) => {
    const filtersRef = useRef(null);
    const elRef = useRef(null);
    const [filterOpen, setFilterOpen] = useState(false);
    const [sort, setSort] = useState('latest');
    const state = useSelector(state => state);
    const selectedFilters = getFilters(state);
    // const router = useRouter();

    const reportsFromApi = getReports(state);
    const reportsLoading = getReportsLoading(state);

    const dispatch = useDispatch();
    const handleFilterSelect = e => {
        const type = e.currentTarget.name;
        const value = e.currentTarget.value;
        const label = e.currentTarget.getAttribute('data-label');
        const obj = { type: type, value: value, label: label };
        if (value === 'all') {
            dispatch(clearFilters(type));
        } else {
            if (selectedFilters.length < 8) dispatch(addFilter(obj));
            else dispatch(removeFilter(obj));
        }
    };

    const [reportsCache, setReportsCache] = useState(reports);
    const [filterCache, setFilterCache] = useState(selectedFilters);

    const sortSelect = [
        {
            label: copies.sortLatestLabel,
            value: 'latest',
        },
        {
            label: copies.sortAZLabel,
            value: 'a_z',
        },
        {
            label: copies.sortZALabel,
            value: 'z_a',
        },
    ];

    const removeThisFilter = e => {
        const type = e.currentTarget.getAttribute('data-type');
        const value = e.currentTarget.getAttribute('data-value');
        const label = e.currentTarget.getAttribute('data-label');
        const obj = { type: type, value: value, label: label };
        dispatch(addFilter(obj));
    };

    const clearFiltersClick = () => {
        dispatch(clearFilters(''));
    };

    const filterClick = () => {
        setFilterOpen(true);
    };

    const handlePageChange = page => {
        dispatch(fetchReports(page, convertFiltersToString(), sort));
    };

    const convertFiltersToString = () => {
        const obj = {};
        selectedFilters.forEach(f => {
            if (!obj[f.type]) {
                obj[f.type] = [f.value];
            } else {
                obj[f.type].push(f.value);
            }
        });
        return JSON.stringify(obj);
    };

    useEffect(() => {
        if (JSON.stringify(filterCache) !== JSON.stringify(selectedFilters)) {
            setFilterCache(selectedFilters);
        }
    }, [selectedFilters]);

    const isFirstRun = useRef(true);
    const router = useRouter();

    useEffect(() => {
        isFirstRun.current = true;
        isFirstSort.current = true;
        return () => {
            setReportsCache(reports);
            dispatch(clearFilters(''));
        };
    }, [router.pathname]);

    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        dispatch(fetchReports(0, convertFiltersToString(), sort));
    }, [filterCache]);

    const startLoading = useRef(false);

    useEffect(() => {
        if (reportsLoading) {
            startLoading.current = true;
        }
        if (
            startLoading.current &&
            !reportsLoading &&
            reportsFromApi?.data !== undefined &&
            JSON.stringify(reportsFromApi) !== JSON.stringify(reportsCache)
        ) {
            setReportsCache(reportsFromApi);
            scrollToTargetOffset(elRef.current, isDesktop ? 110 : 80);
            startLoading.current = false;
        }
    }, [reportsLoading]);

    const isFirstLoad = useRef(true);

    useEffect(() => {
        if (isFirstLoad.current) {
            filters.map(type => {
                if (router.query[type.type] !== undefined) {
                    const queryFilters = router.query[type.type].split(',');
                    queryFilters.map(queryFilter => {
                        const hasFilter = type.data.filter(f => f.value === queryFilter);
                        if (hasFilter.length > 0) {
                            const obj = { type: type.type, value: hasFilter[0].value, label: hasFilter[0].label };
                            dispatch(addFilter(obj));
                        }
                        return false;
                    });
                }
                return false;
            });
            isFirstLoad.current = false;
            return;
        }
        return () => {
            // clearAllBodyScrollLocks();
        };
    });

    useEffect(() => {
        // setPageLoaded(true);
        // window.addEventListener('resize', () => setFilterOpen(false));
        return () => {
            // clearAllBodyScrollLocks();
        };
    });

    useEffect(() => {
        if (filterOpen) {
            setBodyNoScroll(true);
        } else {
            setBodyNoScroll(false);
        }
        return () => {
            setBodyNoScroll(false);
        };
    }, [filterOpen]);

    const isDesktop = useMediaQuery('only screen and (min-width: 768px)');

    useEffect(() => {
        if (isDesktop) {
            setFilterOpen(false);
        }
    }, [isDesktop]);

    const RenderSelectedFilters = () => (
        <>
            {selectedFilters.map((filter, i) => (
                <button
                    key={i}
                    type='button'
                    data-type={filter.type}
                    data-label={filter.label}
                    data-value={filter.value}
                    className={style.filterButton}
                    onClick={removeThisFilter}
                >
                    {filter.label}
                    <span className={style.cross} />
                </button>
            ))}
        </>
    );

    const handleSortChange = value => {
        const val = typeof value === 'string' ? value : value.target.value;
        if (val !== sort) {
            setSort(val);
        }
    };

    const isFirstSort = useRef(true);

    useEffect(() => {
        if (isFirstSort.current) {
            isFirstSort.current = false;
            return;
        }
        dispatch(fetchReports(0, convertFiltersToString(), sort));
    }, [sort]);

    return (
        <section className={style.el} ref={elRef}>
            <div className={[style.container, 'container'].join(' ')}>
                <div className={style.filters}>
                    <div className={style.filtersMobile}>
                        {selectedFilters?.length > 0 ? (
                            <button type='button' className={style.clearButton} onClick={clearFiltersClick}>
                                <span className='icon-close' />
                                {copies?.clear}
                            </button>
                        ) : (
                            <div />
                        )}
                        <button type='button' className={style.filterButton} onClick={filterClick}>
                            <span className='icon-filter' />
                            {copies?.filter}
                        </button>
                    </div>
                    <div className={[style.filtersWrapper, filterOpen ? style.open : ''].join(' ')} ref={filtersRef}>
                        <div className={style.filterHeadMobile}>
                            <button className={style.backBtn} onClick={() => setFilterOpen(false)}>
                                {/* <span className='icon-arrow-right' /> */}
                                <span className='sr-only'>back</span>
                            </button>
                            <button className={style.closeBtn} onClick={() => setFilterOpen(false)}>
                                <span className='icon-close' />
                                <span className='sr-only'>close</span>
                            </button>
                        </div>
                        {selectedFilters?.length > 0 && (
                            <div className={[style.selectedFilters, 'mobile'].join(' ')}>
                                <RenderSelectedFilters />
                            </div>
                        )}
                        {filters?.map((item, i) => (
                            <div
                                className={style.filterSelectWrap}
                                style={{ width: `${100 / filters.length}%` }}
                                key={item.type}
                            >
                                <FilterSelect
                                    {...item}
                                    handleSelect={handleFilterSelect}
                                    selectedFilters={Array.from(selectedFilters).filter(f => f.type === item.type)}
                                />
                            </div>
                        ))}
                        <div className={style.filterBottomMobile}>
                            <button className={style.applyBtn} onClick={() => setFilterOpen(false)}>
                                {copies?.apply}
                            </button>
                        </div>
                    </div>
                </div>
                <div className={style.filtersSort}>
                    <div className={[style.selectedFilters, 'desktop'].join(' ')}>
                        {selectedFilters.length > 0 && <RenderSelectedFilters />}
                    </div>
                    <div className={[style.numberTeamMembersMobile, 'mobile'].join(' ')}>
                        {copies?.countLabel && (
                            <>
                                <span className={style.number}>{reportsCache.totalItems}</span> {copies?.countLabel}
                            </>
                        )}
                    </div>
                    <div className={style.sortSelect}>
                        <CustomSelect
                            active={sort}
                            data={sortSelect}
                            defaultTitle={copies?.sortBy}
                            handleSelect={handleSortChange}
                        />
                    </div>
                </div>
                <div className={[style.numberTeamMembers, 'desktop'].join(' ')}>
                    <div>
                        {copies?.countLabel && (
                            <>
                                <span className={style.number}>{reportsCache.totalItems}</span> {copies?.countLabel}
                            </>
                        )}
                    </div>
                    {selectedFilters.length > 0 && (
                        <button
                            type='button'
                            className={[style.clearButton, 'desktop'].join(' ')}
                            onClick={clearFiltersClick}
                        >
                            <span className='icon-close' />
                            {copies?.clearFilters}
                        </button>
                    )}
                </div>
                <div className={style.newsWrapper}>
                    {reportsCache.data.length > 0 ? (
                        reportsCache.data.map((item, i) => (
                            <div className={style.reportCard} key={`report${i}`}>
                                <ReportCard {...item} selectedFilters={selectedFilters} />
                            </div>
                        ))
                    ) : (
                        <div className={style.noResults}>{copies?.noResultMessage}</div>
                    )}
                </div>
                {reportsCache.totalPages > 0 && (
                    <Pagination
                        total={reportsCache.totalPages + 1}
                        active={reportsCache.currentPage}
                        changePage={handlePageChange}
                    />
                )}
            </div>
        </section>
    );
};

ReportListing.propTypes = {
    filters: PropTypes.array,
    reports: PropTypes.object,
    copies: PropTypes.object,
};

export default ReportListing;
